import { assign, setup } from 'xstate';
import type { ApolloClient } from '@apollo/client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import type { GetQuickSearchResultsV2QueryVariables } from '../gql/generated/GetQuickSearchResultsV2';
import type { QuickSearchBaseContext } from './types';
import { fetchLexicalQueryActor, sharedGuards } from './utils';

const singleGuards = {
  ...sharedGuards,
  hasInitialNodes: ({ context }: { context: QuickSearchBaseContext }) =>
    context.lexicalComplete && context.lexicalNodes.length > 0,
};

export type QuickSearchSingleEvents =
  | {
      type: 'SEARCH';
      requestVariables: GetQuickSearchResultsV2QueryVariables;
      apolloClient?: ApolloClient<unknown>;
    }
  | {
      type: 'RESET';
    };

export type QuickSearchSingleMachineInput = {
  apolloClient: ApolloClient<unknown>;
  requestVariables: QuickSearchBaseContext['requestVariables'];
  initialLexicalNodes?: QuickSearchResult[]; // For hydration - pre-populate results
};

/**
 * Quick Search State Machine - SINGLE Mode
 */
export const quickSearchSingleMachine = setup({
  types: {
    context: {} as QuickSearchBaseContext,
    events: {} as QuickSearchSingleEvents,
    input: {} as QuickSearchSingleMachineInput,
  },
  // ==================== GUARDS ====================
  guards: singleGuards,
  // ==================== ACTIONS ====================
  actions: {
    updateSearchParams: assign({
      requestVariables: ({ event }) => {
        if (event.type === 'SEARCH') {
          return event.requestVariables;
        }
        return undefined as never;
      },
      apolloClient: ({ event, context }) => {
        if (event.type === 'SEARCH' && event.apolloClient) {
          return event.apolloClient;
        }
        return context.apolloClient;
      },
      lexicalNodes: ({ event }) => {
        if (event.type === 'SEARCH') {
          return [];
        }
        return undefined as never;
      },
      lexicalComplete: () => false,
    }),
    clearResults: assign({
      lexicalNodes: () => [],
      lexicalError: () => undefined,
      lexicalComplete: () => false,
    }),
    storeLexicalSuccess: assign({
      lexicalNodes: ({ event }) => {
        if ('output' in event) {
          return event.output as QuickSearchResult[];
        }
        return [];
      },
      lexicalError: () => undefined,
      lexicalComplete: () => true,
    }),
    storeLexicalError: assign({
      lexicalNodes: () => [],
      lexicalError: ({ event }) => {
        if ('error' in event) {
          return event.error as Error;
        }
        return undefined;
      },
      lexicalComplete: () => true,
    }),
  },
  // ==================== ACTORS ====================
  actors: {
    fetchLexicalQuery: fetchLexicalQueryActor,
  },
}).createMachine({
  id: 'quickSearchSingle',
  initial: 'initializing',
  context: ({ input }: { input: QuickSearchSingleMachineInput }) => ({
    requestVariables: input.requestVariables,
    lexicalNodes: input.initialLexicalNodes ?? [],
    lexicalComplete: input.initialLexicalNodes !== undefined,
    apolloClient: input.apolloClient,
  }),
  // ==================== STATES ====================
  states: {
    initializing: {
      // Immediately transition to complete if we have cached data, otherwise we transition to our initial idle state
      always: [
        {
          guard: 'hasInitialNodes',
          target: 'complete',
        },
        {
          target: 'idle',
        },
      ],
    },
    idle: {
      on: {
        SEARCH: {
          target: 'loading',
          actions: ['updateSearchParams'],
        },
      },
    },
    loading: {
      invoke: {
        id: 'lexicalQueryActor',
        src: 'fetchLexicalQuery',
        input: ({ context }) => ({
          apolloClient: context.apolloClient as ApolloClient<unknown>,
          requestVariables: context.requestVariables,
        }),
        onDone: {
          target: 'complete',
          actions: ['storeLexicalSuccess'],
        },
        onError: {
          target: 'complete',
          actions: ['storeLexicalError'],
        },
      },
      on: {
        SEARCH: {
          target: 'loading',
          actions: ['updateSearchParams'],
        },
      },
    },
    complete: {
      // Final state representing completed query (success, error, or no results)
      // Outcome is determined by context: lexicalNodes, lexicalError, or lexicalComplete
      on: {
        SEARCH: {
          target: 'loading',
          actions: ['updateSearchParams'],
        },
      },
    },
  },
  on: {
    RESET: {
      target: '.idle',
      actions: ['clearResults'],
    },
  },
});
